import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export interface BlockIdOptions {
  //only addresses top-level children of the doc.
  schemaTypes: string[];
  /**
   * Subset of `schemaTypes` for which this extension should declare the `id`
   * attribute on the schema. Types whose own extensions already declare an
   * `id` attribute (e.g. `image`, `imagePlaceholder`) must be omitted to avoid
   * a schema clash; the runtime plugin still ensures values are minted for
   * them.
   */
  ownedTypes: string[];
  generateId: () => string;
}

const blockIdPluginKey = new PluginKey('blockId');

const DEFAULT_SCHEMA_TYPES = [
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'taskList',
  'blockquote',
  'codeBlock',
  'horizontalRule',
  'table',
  'image',
  'imagePlaceholder',
];

const DEFAULT_OWNED_TYPES = [
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'taskList',
  'blockquote',
  'codeBlock',
  'horizontalRule',
  'table',
];

export const BlockIdExtension = Extension.create<BlockIdOptions>({
  name: 'blockId',

  addOptions() {
    return {
      schemaTypes: DEFAULT_SCHEMA_TYPES,
      ownedTypes: DEFAULT_OWNED_TYPES,
      generateId: () => crypto.randomUUID(),
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.ownedTypes,
        attributes: {
          id: {
            default: null,
            keepOnSplit: false,
            rendered: false,
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const { schemaTypes, generateId } = this.options;
    return [
      new Plugin({
        key: blockIdPluginKey,
        appendTransaction: (transactions, oldState, newState) => {
          const docChanged =
            transactions.some((t) => t.docChanged) && !oldState.doc.eq(newState.doc);
          const isInit = transactions.length === 0;
          if (!docChanged && !isInit) return null;

          const seen = new Set<string>();
          const tr = newState.tr;
          let modified = false;

          newState.doc.forEach((node, offset) => {
            if (!schemaTypes.includes(node.type.name)) return;
            const currentId = node.attrs?.id;
            if (typeof currentId === 'string' && currentId.length > 0 && !seen.has(currentId)) {
              seen.add(currentId);
              return;
            }
            const nextId = generateId();
            seen.add(nextId);
            tr.setNodeMarkup(offset, undefined, { ...node.attrs, id: nextId });
            modified = true;
          });

          if (!modified) return null;
          tr.setMeta('addToHistory', false);
          return tr;
        },
      }),
    ];
  },
});

export default BlockIdExtension;
